import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, TextField, Switch, Button, Paper, FormControlLabel } from '@mui/material';
import { axiosClient } from "../api/proxy/AxiosClient";
import { updateSettings } from '../redux/settingsSlice';
import TimelineRange from './TimelineRange';

export default function Settings({ user }) {

  const [settings, setSettings] = useState([]);
  const [changed, setChanged] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    async function load() {
      const res = await axiosClient.get('/users/settings');
      setSettings(res.data || []);
    }
    load();
  }, []);

  const handleChange = (settingName, type, value) => {
    setSettings(prev => prev.map(s => s.settingName === settingName ? { ...s, settingValue: value } : s));
    setChanged(prev => ({ ...prev, [settingName]: true }));
  };

  const handleSave = async () => {
    const changedSettings = settings.filter(s => changed[s.settingName]);
    try {
      await axiosClient.post('/users/settings', changedSettings);
      dispatch(updateSettings(changedSettings));
      setChanged({});
    } catch (e) {
        console.error("Error saving settings:", e);
    }
  };

  const renderInput = (setting, readOnly) => {
    const isDefault = setting.id === 0;

    switch (setting.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            value={setting.settingValue}
            disabled={readOnly}
            onChange={(e) => handleChange(setting.settingName, setting.type, e.target.value)}
            label={`${setting.settingName} ${isDefault ? '(default, unsaved)' : ''}`}
          />
        );
      case 'number':
        return (
          <TextField
            type="number"
            fullWidth
            value={setting.settingValue}
            disabled={readOnly}
            onChange={(e) => handleChange(setting.settingName, setting.type, e.target.value)}
            label={`${setting.settingName} ${isDefault ? '(default, unsaved)' : ''}`}
          />
        );
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={setting.settingValue === 'true'}
                disabled={readOnly}
                onChange={(e) => handleChange(setting.settingName, setting.type, e.target.checked.toString())}
              />
            }
            label={`${setting.settingName} ${isDefault ? '(default, unsaved)' : ''}`}
          />
        );
      case 'time_range':
        let range = [];
        const s = setting.settingValue;
        try {
          range = JSON.parse(s);
          if (!Array.isArray(range)) {
            range = s;
          } else {
            range = [range];
          }
        } catch (err) {
          range = s;
        }
        return (
          <TimelineRange
            value={range}
            onChange={(range) => handleChange(setting.settingName, setting.type, range)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>User Settings</Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        {settings.map(setting => (
          <Paper key={`${setting.type}_${setting.settingName}`} sx={{ p: 2 }}>
            {renderInput(setting, user.isDemo || setting.isReadonly)}
          </Paper>
        ))}
      </Box>
      <Box mt={3}>
        <Button variant="contained"
                onClick={handleSave}
                disabled={user.isDemo}>Save Changes</Button>
      </Box>
    </Box>
  );
}
